/**
 * The card a change "save this as a template" opens (§11.2, §21.16, P22-T05):
 * the change ends with `template_card {name, template_id, slots}` — the
 * template is already made from the current version — and the page shows a
 * small card with its name and the parts that change (their labels when the
 * templates' list has them, else their number), linking to a new ad made
 * from it (`/new?template=<id>`).
 */
import { Link } from "react-router-dom";
import type { Said } from "../api/changes";
import { TEMPLATE_CARD, fillAll } from "../copy/changes";
import { Button } from "../ui";

export interface TemplateCardInfo {
  name: string;
  templateId: string;
  slots: number;
}

/** The card's parameters from a change's `says`, or null when it has none. */
export function templateCardOf(says: Said[]): TemplateCardInfo | null {
  const said = says.find((s) => s.code === "template_card");
  const p = said?.params ?? {};
  if (typeof p.template_id !== "string" || typeof p.name !== "string") {
    return null;
  }
  return {
    name: p.name,
    templateId: p.template_id,
    slots: typeof p.slots === "number" ? p.slots : 0,
  };
}

export function TemplateCard({
  card,
  labels,
  onClose,
}: {
  card: TemplateCardInfo;
  /** The slots' labels from `GET /v1/templates`, or null while unknown. */
  labels: string[] | null;
  onClose(): void;
}) {
  return (
    <section
      aria-label={TEMPLATE_CARD.templateTitle}
      className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3"
      data-testid="template-card"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {TEMPLATE_CARD.templateTitle}
          </span>
          <span className="break-words text-sm font-semibold text-slate-900">
            {card.name}
          </span>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          {TEMPLATE_CARD.close}
        </Button>
      </div>
      {labels && labels.length ? (
        <div className="flex flex-col gap-1 text-sm text-slate-700">
          <span>{TEMPLATE_CARD.templateSlots}</span>
          <ul className="m-0 flex list-none flex-wrap gap-1 p-0">
            {labels.map((label, i) => (
              <li
                key={`${i}-${label}`}
                className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
              >
                {label}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="m-0 text-sm text-slate-700">
          {card.slots === 1
            ? TEMPLATE_CARD.templateCountOne
            : (fillAll(TEMPLATE_CARD.templateCount, { slots: card.slots }) ??
              "")}
        </p>
      )}
      <Link
        to={`/new?template=${encodeURIComponent(card.templateId)}`}
        className="text-sm font-semibold underline"
      >
        {TEMPLATE_CARD.templateOpen}
      </Link>
    </section>
  );
}
